// Self-hosted Geist. First so the faces are declared before any component style
// that references the family.
import './assets/fonts.css';

import 'vue-pdf-embed/dist/styles/annotationLayer.css';
import 'vue-pdf-embed/dist/styles/textLayer.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import naive from 'naive-ui';
import router from './router';
import { configureApi } from './config/api';
import { isAccessDenied, NetworkError } from './utils/errorHandler';
import VueHotjar from 'vue-hotjar-next';
import { createGtag } from 'vue-gtag';
import { CkeditorPlugin } from '@ckeditor/ckeditor5-vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import { isOfficeAddinHost } from './office/utils';
import PrimeVue from 'primevue/config';
import 'primevue/resources/primevue.min.css';
import 'primevue/resources/themes/aura-light-noir/theme.css';

import * as Sentry from '@sentry/vue';

async function bootstrap(): Promise<void> {
  const app = createApp(App);

  // Report only from deployed staging/prod builds. import.meta.env.PROD is false on every
  // local dev server, so `pnpm dev` and `pnpm local_staging` (which sets VITE_ENV=staging)
  // never initialise the SDK and send nothing to sentry.io.
  const environment = import.meta.env.VITE_ENV;
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (
    import.meta.env.PROD &&
    (environment === 'staging' || environment === 'prod') &&
    sentryDsn !== undefined
  ) {
    Sentry.init({
      app,
      dsn: sentryDsn,
      environment,
      dataCollection: {},
      enableLogs: true,
      // Network failures are transient client-side conditions, not app bugs:
      // group them per endpoint (not per caller's stack) and keep them out of
      // error-level alerting.
      beforeSend(event, hint) {
        // Also match a NetworkError a caller wrapped as `cause`.
        const original = hint.originalException;
        // The backend refusing an object (no access, or not in the active
        // organisation). Views that render this log it as a warning and never
        // get here; what still does is kept, but out of error-level alerting.
        if (isAccessDenied(original)) {
          event.level = 'warning';
          return event;
        }
        const error =
          original instanceof Error && original.cause instanceof NetworkError
            ? original.cause
            : original;
        if (error instanceof NetworkError) {
          event.level = 'warning';
          event.fingerprint = ['network-error', error.endpoint];
          const exception = event.exception?.values?.find(value => value.type === 'NetworkError');
          if (exception) exception.value = error.detail;
        }
        return event;
      },
    });
  }

  // Wait for Office context to be fully ready before mounting.
  // This ensures window.Office.context.ui is populated so isOfficeAddinHost()
  // returns the correct value and the Google sign-in dialog API is available.
  if (window.Office !== undefined) {
    await window.Office.onReady();
  }

  const pinia = createPinia();

  app.use(pinia);
  app.use(naive);
  app.use(router);
  app.use(CkeditorPlugin);

  app.use(PrimeVue, {
    // unstyled: true, // enable this if you want to completely control styling yourself
  });

  if (import.meta.env.VITE_HOTJAR_ID) {
    app.use(VueHotjar, {
      id: import.meta.env.VITE_HOTJAR_ID,
      isProduction: true,
      snippetVersion: 6,
    });
  }

  if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
    const gtag = createGtag({
      tagId: import.meta.env.VITE_GA_MEASUREMENT_ID,
    });
    app.use(gtag);
  }

  if (!isOfficeAddinHost()) {
    app.use(VueReCaptcha, {
      siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
      loaderOptions: {
        useRecaptchaNet: true,
        useEnterprise: true,
        autoHideBadge: true,
      },
    });
  }

  // Configure API client after pinia is installed
  configureApi();

  // Mount the app. Auth/bootstrap side effects (auth store, prompt library,
  // gaia token, Hotjar identify) are owned by the authenticated layouts via
  // useAuthenticatedBootstrap() — nothing auth-related may run here, so public
  // pages (e.g. /external/...) stay free of Firebase and account requests.
  app.mount('#app');
}

// Top-level await breaks the configured production browser targets.
// eslint-disable-next-line unicorn/prefer-top-level-await
void bootstrap();
